import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { toAppError } from "@/services/errors";

/**
 * Notifications.
 *
 * The only place an error becomes visible. Everything thrown by a callable is
 * an `AppError` carrying an i18n key, and this turns the key into a sentence in
 * the reader's language — so a raw Firebase code cannot reach a teacher even if
 * a call site forgets to handle it.
 */

export type ToastTone = "success" | "error" | "info";

export interface Toast {
  id: number;
  tone: ToastTone;
  /** An i18n key, never prose. */
  key: string;
  params: Record<string, unknown>;
  /** How many times the same message has arrived. Shown from two upwards. */
  count: number;
}

interface ToastValue {
  toasts: Toast[];
  success: (key: string, params?: Record<string, unknown>) => void;
  info: (key: string, params?: Record<string, unknown>) => void;
  error: (key: string, params?: Record<string, unknown>) => void;
  /** Takes anything thrown and shows the right sentence. */
  showError: (error: unknown) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastValue | undefined>(undefined);

const DISMISS_AFTER_MS = 6000;

/**
 * How many can be on screen at once.
 *
 * Four clicks on a broken report produced five identical notes stacked over
 * the page, covering the "Try again" button underneath them (live run,
 * 2026-09-09). Repeats now count up in place, and the stack has a ceiling: a
 * notification that hides the control it is about is worse than no
 * notification.
 */
const MAX_VISIBLE = 3;

/** The same message, said again — not a second message. */
function isSame(toast: Toast, tone: ToastTone, key: string, params: Record<string, unknown>) {
  return (
    toast.tone === tone &&
    toast.key === key &&
    JSON.stringify(toast.params) === JSON.stringify(params)
  );
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, key: string, params: Record<string, unknown>) => {
      const id = nextId.current++;

      setToasts((current) => {
        const existing = current.find((toast) => isSame(toast, tone, key, params));
        if (existing) {
          return current.map((toast) =>
            toast.id === existing.id ? { ...toast, count: toast.count + 1 } : toast
          );
        }
        // The oldest goes when the ceiling is reached: the newest note is the
        // one the reader's last action produced.
        const kept = current.slice(Math.max(0, current.length - (MAX_VISIBLE - 1)));
        return [...kept, { id, tone, key, params, count: 1 }];
      });

      // Errors stay until dismissed. A teacher who looked away for four seconds
      // should not have to guess why an action did nothing.
      if (tone !== "error") {
        window.setTimeout(() => dismiss(id), DISMISS_AFTER_MS);
      }
    },
    [dismiss]
  );

  /*
    The actions are memoised apart from the list.

    Their identity has to be stable: every hook that reports an error puts
    `showError` in a dependency array, and if that identity changed whenever a
    toast appeared, a call that fails would show a toast, change the identity,
    re-run the effect, call again, and fail again — an infinite request loop
    triggered by the first callable whose refusal is ordinary. One was.
  */
  const actions = useMemo(
    () => ({
      success: (key: string, params?: Record<string, unknown>) =>
        push("success", key, params ?? {}),
      info: (key: string, params?: Record<string, unknown>) => push("info", key, params ?? {}),
      error: (key: string, params?: Record<string, unknown>) => push("error", key, params ?? {}),
      showError: (error: unknown) => {
        const appError = toAppError(error);
        push("error", appError.key, appError.params);
      },
      dismiss,
    }),
    [push, dismiss]
  );

  const value = useMemo<ToastValue>(() => ({ toasts, ...actions }), [toasts, actions]);

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

export function useToast(): ToastValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside ToastProvider");
  return value;
}
